import { ReactNode } from 'react'
import { useRouter } from 'next/router'
import { useCookies } from 'react-cookie'

import { store } from 'app-redux/store'

import { LegacyIcons } from 'components/elements/Icon'
import { CustomIcons } from 'components/elements/CustomIcon'
import { ManaFontIcons } from 'components/misc/customIcons/ManaFont'

import { useCanEditDeck } from 'app-redux/deck/selectors'
import { useRightPanel } from 'hooks/usePinnedSidecar'
import { useActionless, useAppSelector } from 'app-redux/hooks'

import { SET_ACTIVE_STATE, TOGGLE_MODAL } from 'app-redux/active/actions/types'
import { SET_DECK_STATE } from 'app-redux/deck/actions/types'

import BookmarkService from 'services/bookmark.service'
import ToastService from 'services/toast.service'

import Image from 'components/elements/Image'

import { FORMATS_WITH_PRECONSTRUCTED } from 'components/deckPage/misc/EmptyDeckLanding/buttons/ImportFromPrecon'
import { generateDeckAsQueryParam } from 'utils/deckAsQueryParam'
import { EDH_FORMATS } from 'types/deck'

export type DeckAction =
  | 'cardSearch'
  | 'import'
  | 'recs'
  | 'landbase'
  | 'packages'
  | 'precons'
  | 'customCards'
  | 'optimizePrintings'
  | 'optimizeBasics'
  | 'export'
  | 'buySell'
  | 'stats'
  | 'bookmark'
  | 'combos'
  | 'history'
  | 'addRemoveCards'
  | 'snapshots'
  | 'quickSnapshot'
  | 'recentChanges'
  | 'settings'
  | 'mainSettings'
  | 'description'
  | 'collaborators'
  | 'colorTags'
  | 'categories'
  | 'deckTags'
  | 'clone'
  | 'share'
  | 'compare'
  | 'addToCollection'
  | 'keybinds'
  | 'selectAll'
  | 'report'
  | 'playtester'
  | 'containingFolder'
  | 'sandbox'
  | 'deckHelp'

type Action = {
  label: string

  to?: string
  onClick?: () => void

  icon?: LegacyIcons
  customIcon?: CustomIcons
  manaIcon?: ManaFontIcons
  iconElement?: ReactNode

  keybind?: string
  disabled?: boolean
  hidden?: boolean

  className?: string
}

const useDeckActions = (onClick?: () => void): Record<DeckAction, Action> => {
  const router = useRouter()

  const canEditDeck = useCanEditDeck()

  const [{ tbId: userId }] = useCookies(['tbId'])

  const deckId = useAppSelector(state => state.deck.id)
  const ownerId = useAppSelector(state => state.deck.ownerid)
  const parentFolder = useAppSelector(state => state.deck.parentFolder)
  const deckFormat = useAppSelector(state => state.deck.format)
  const bookmarked = useAppSelector(state => state.deck.bookmarked)

  const snapshotMeta = useAppSelector(state => state.deck.snapshotMeta)

  const [rightPanelStatus, setRightPanelStatus] = useRightPanel()

  const [toggleModal, setActiveState, setDeckState] = useActionless(TOGGLE_MODAL, SET_ACTIVE_STATE, SET_DECK_STATE)

  const ownsDeck = Number(userId) === Number(ownerId)

  const handleRouteSandboxToPlaytester = () => {
    const deckAsQueryParam = generateDeckAsQueryParam()

    window.open(`/playtester-v2/sandbox?deck=${deckAsQueryParam}`, '_ blank')
  }

  const handleToggleBookmark = () => {
    if (!deckId) return

    if (bookmarked)
      return BookmarkService.remove(deckId)
        .then(() => ToastService.create('Bookmark removed', 'Bookmark service', 'success'))
        .then(() => setDeckState({ bookmarked: false }))
        .catch(() => ToastService.create('Unable to delete bookmark', 'Bookmark service'))

    return BookmarkService.create(deckId)
      .then(() => ToastService.create('Deck bookmarked', 'Bookmark service', 'success'))
      .then(() => setDeckState({ bookmarked: true }))
      .catch(() => ToastService.create('Unable to create bookmark', 'Bookmark service'))
  }

  const handleSelectAll = (optimizeCardOverlay = false) => {
    const { cardMap } = store.getState().deck
    const multiSelectedIds = Object.keys(cardMap).reduce((a, id) => ({ ...a, [id]: true }), {})

    setActiveState({ multiSelectedIds, optimizeCardOverlay })
  }

  const handleClickInjection = (action: DeckAction) => {
    if (action === 'cardSearch') setActiveState({ searchOpen: 'search' })
    if (action === 'import') toggleModal({ modalName: 'massEditModal', flag: true })
    if (action === 'recs') setActiveState({ searchOpen: 'recs' })
    if (action === 'landbase') setActiveState({ searchOpen: 'manabaseGenerator' })
    if (action === 'packages') toggleModal({ modalName: 'cardPackageModal', flag: true })
    if (action === 'precons') setActiveState({ preconsOverlay: true })
    if (action === 'customCards') setActiveState({ selectCustomCards: true })
    if (action === 'optimizePrintings') handleSelectAll(true)
    if (action === 'optimizeBasics') setActiveState({ autofillBasicLandsOverlay: true })
    if (action === 'export') toggleModal({ modalName: 'export', flag: true })
    if (action === 'buySell') toggleModal({ modalName: 'bulkBuy', flag: true })
    if (action === 'stats') setRightPanelStatus({ ...rightPanelStatus, open: true })
    if (action === 'bookmark') handleToggleBookmark()
    if (action === 'combos') setActiveState({ searchOpen: 'spellbookCombo' })
    if (action === 'history' || action === 'addRemoveCards') toggleModal({ modalName: 'addRemovedCards', flag: true })
    if (action === 'recentChanges') toggleModal({ modalName: 'recentChanges', flag: true })
    if (action === 'snapshots') toggleModal({ modalName: 'snapshots', flag: true })
    if (action === 'quickSnapshot') toggleModal({ modalName: 'createSnapshotStandalone', flag: true })
    if (action === 'settings' || action === 'mainSettings') toggleModal({ modalName: 'deckSettings', flag: true })
    if (action === 'description') toggleModal({ modalName: 'description', flag: true })
    if (action === 'collaborators') toggleModal({ modalName: 'collaborators', flag: true })
    if (action === 'deckTags') toggleModal({ modalName: 'deckTags', flag: true })
    if (action === 'colorTags') toggleModal({ modalName: 'colorTags', flag: true })
    if (action === 'categories') toggleModal({ modalName: 'categories', flag: true })
    if (action === 'clone') toggleModal({ modalName: 'cloneModal', flag: true })
    if (action === 'share') toggleModal({ modalName: 'shareDeck', flag: true })
    if (action === 'addToCollection') toggleModal({ modalName: 'addToCollection', flag: true })
    if (action === 'keybinds') toggleModal({ modalName: 'deckKeybinds', flag: true })
    if (action === 'report') toggleModal({ modalName: 'reportDeck', flag: true })
    if (action === 'selectAll') handleSelectAll()
    if (action === 'playtester') handleRouteSandboxToPlaytester()
    if (action === 'deckHelp') toggleModal({ modalName: 'deckHelp', flag: true })

    if (onClick) onClick()
  }

  const actions: Record<DeckAction, Action> = {
    cardSearch: {
      label: 'Card search',
      icon: 'search',
      onClick: () => handleClickInjection('cardSearch'),
    },
    import: {
      label: 'Import cards',
      icon: 'upload',
      onClick: () => handleClickInjection('import'),
      hidden: !canEditDeck,
    },
    recs: {
      label: 'EDH Recs',
      customIcon: 'edhrec',
      onClick: () => handleClickInjection('recs'),
      hidden: !EDH_FORMATS.includes(deckFormat),
    },
    landbase: {
      label: 'Landbase',
      manaIcon: 'c',
      onClick: () => handleClickInjection('landbase'),
    },
    packages: {
      label: 'Card packages',
      icon: 'cube',
      onClick: () => handleClickInjection('packages'),
    },
    precons: {
      label: 'Add precon to deck',
      icon: 'cloud download',
      hidden: !canEditDeck || !FORMATS_WITH_PRECONSTRUCTED.includes(deckFormat),
      onClick: () => handleClickInjection('precons'),
    },
    customCards: {
      label: 'Add custom card',
      icon: 'file image outline',
      hidden: !ownsDeck || !!snapshotMeta,
      onClick: () => handleClickInjection('customCards'),
    },
    optimizePrintings: {
      label: 'Optimize printings',
      icon: 'money bill alternate outline',
      onClick: () => handleClickInjection('optimizePrintings'),
    },
    optimizeBasics: {
      label: 'Optimize basics',
      hidden: !canEditDeck,
      manaIcon: 'tap',
      onClick: () => handleClickInjection('optimizeBasics'),
    },
    export: {
      label: 'Export deck',
      icon: 'download',
      onClick: () => handleClickInjection('export'),
    },
    buySell: {
      label: 'Buy / sell',
      icon: 'shopping cart',
      onClick: () => handleClickInjection('buySell'),
    },
    stats: {
      label: 'Deck stats',
      icon: 'pie graph',
      disabled: rightPanelStatus.locked,
      keybind: 'Ctrl + M',
      onClick: () => handleClickInjection('stats'),
    },
    bookmark: {
      label: bookmarked ? 'Remove bookmark' : 'Bookmark deck',
      icon: bookmarked ? 'remove bookmark' : 'bookmark outline',
      hidden: !userId,
      onClick: () => handleClickInjection('bookmark'),
    },
    combos: {
      label: 'Commander combos',
      iconElement: <Image alt="Commander Spellbook" src="/images/spellbook_simple.svg" height={15} />,
      onClick: () => handleClickInjection('combos'),
      hidden: !EDH_FORMATS.includes(deckFormat),
    },
    history: {
      label: 'Deck history',
      icon: 'history',
      onClick: () => handleClickInjection('history'),
    },

    // Deck history tabs
    addRemoveCards: {
      label: 'Add / remove cards',
      onClick: () => handleClickInjection('addRemoveCards'),
    },
    recentChanges: {
      label: 'Recent changes',
      onClick: () => handleClickInjection('recentChanges'),
    },
    snapshots: {
      label: 'Snapshots',
      onClick: () => handleClickInjection('snapshots'),
    },
    quickSnapshot: {
      label: 'Create snapshot',
      icon: 'code branch',
      onClick: () => handleClickInjection('quickSnapshot'),
      hidden: !canEditDeck,
    },

    settings: {
      label: 'Deck settings',
      icon: 'cog',
      hidden: !canEditDeck,
      onClick: () => handleClickInjection('settings'),
    },

    // Deck setting tabs
    mainSettings: {
      label: 'Main settings',
      hidden: !canEditDeck,
      onClick: () => handleClickInjection('mainSettings'),
    },
    description: {
      label: 'Description / primer',
      hidden: !canEditDeck,
      icon: 'comment outline',
      onClick: () => handleClickInjection('description'),
    },
    collaborators: {
      label: 'Collaborators',
      hidden: !canEditDeck,
      onClick: () => handleClickInjection('collaborators'),
    },
    deckTags: {
      label: 'Deck tags',
      hidden: !canEditDeck,
      onClick: () => handleClickInjection('deckTags'),
    },
    colorTags: {
      label: 'Color tags',
      hidden: !canEditDeck,
      onClick: () => handleClickInjection('colorTags'),
    },
    categories: {
      label: 'Categories',
      hidden: !canEditDeck,
      onClick: () => handleClickInjection('categories'),
    },

    clone: {
      label: 'Clone deck',
      icon: 'copy outline',
      onClick: () => handleClickInjection('clone'),
    },
    share: {
      label: 'Share deck',
      icon: 'share alternate',
      hidden: !deckId,
      onClick: () => handleClickInjection('share'),
    },
    compare: {
      label: 'Compare deck',
      to: `/partialCompare?one=${!!snapshotMeta ? 's_' : 'd_'}${deckId}&diffFields=totalQuantity`,
      icon: 'exchange',
      hidden: !deckId,
    },
    addToCollection: {
      label: 'Add deck to collection',
      icon: 'circle-plus',
      hidden: !userId,
      onClick: () => handleClickInjection('addToCollection'),
    },
    keybinds: {
      label: 'Keybinds',
      icon: 'question circle outline',
      keybind: '?',
      onClick: () => handleClickInjection('keybinds'),
    },
    selectAll: {
      label: 'Select all',
      icon: 'hand pointer outline',
      keybind: 'Ctrl + A',
      onClick: () => handleClickInjection('selectAll'),
    },
    report: {
      label: 'Report deck',
      icon: 'flag outline',
      hidden: canEditDeck || !userId,
      onClick: () => handleClickInjection('report'),
    },
    playtester: {
      label: 'Playtester',
      icon: 'play-circle',
      to: deckId ? `/playtester-v2/${deckId}` : undefined,
      onClick: !deckId ? () => handleClickInjection('playtester') : undefined,
    },
    containingFolder: {
      label: 'Open containing folder',
      icon: 'folder open outline',
      to: `/folders/${parentFolder}`,
      hidden: !ownsDeck,
    },
    sandbox: {
      label: 'Open in sandbox',
      icon: 'flask',
      to: `/sandbox/${deckId}`,
      hidden: !!snapshotMeta || !deckId,
    },
    deckHelp: {
      label: 'Request deck help',
      icon: 'question circle outline',
      onClick: () => handleClickInjection('deckHelp'),
      hidden: !ownsDeck,
    },
  }

  return actions
}

export default useDeckActions
